declare global {
  interface Window {
    Turbo?: {
      renderStreamMessage: (html: string) => void;
    };
  }
}

import {
  createGrid,
  GridOptions,
  GridState,
  ColDef,
  CellRange,
  Column,
  IRowNode,
  CellDoubleClickedEvent,
  StateUpdatedEvent,
  CellSelectionChangedEvent,
  RowSelectedEvent,
  ColumnPivotModeChangedEvent,
} from "ag-grid-enterprise";
import type {
  SideBarDef,
  MenuItemDef,
  DefaultMenuItem,
  ProcessCellForExportParams,
  GetDetailRowDataParams,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { ActionsCellRenderer } from "../ag_grid_shared/cell_renderers/actions_cell_renderer";
import { DropdownActionsCellRenderer } from "../ag_grid_shared/cell_renderers/dropdown_actions_cell_renderer";
import { ElectionNameCellRenderer } from "../ag_grid_shared/cell_renderers/election_name_cell_renderer";
import { IconBadgeCellRenderer } from "../ag_grid_shared/cell_renderers/icon_badge_cell_renderer";
import { ToggleCheckboxCellRenderer } from "../ag_grid_shared/cell_renderers/toggle_checkbox_cell_renderer";
import { LinkCellRenderer } from "../ag_grid_shared/cell_renderers/link_cell_renderer";
import { PostLinkCellRenderer } from "../ag_grid_shared/cell_renderers/post_link_cell_renderer";
import { StatusCellRenderer } from "../ag_grid_shared/cell_renderers/status_cell_renderer";
import { MultilineTextCellRenderer } from "../ag_grid_shared/cell_renderers/multiline_text_cell_renderer";
import { MaskedTextCellRenderer } from "../ag_grid_shared/cell_renderers/masked_text_cell_renderer";
import { SelectedRowCountStatusPanel } from "../ag_grid_shared/status_panels/selected_row_count_status_panel";
import { StatusTestFilter, statusTestFilterHandler } from "../ag_grid_shared/filters/status_test_filter";
import { ebTheme, ebCompactHeaderTheme, ebPlainHeaderTheme } from "../ag_grid_shared/eb_theme";
import { applyErrorFieldCellClassRules } from "../ag_grid_shared/error_field_cell_class_rules";
import { ebFilterParams, ebLocaleText } from "../ag_grid_shared/filter_defaults";
import { formatCellValue, formatGroupCellValue, resolveDisplayExportValue } from "../ag_grid_shared/export_utils";
import { ServerSideDatasource } from "./server_side_datasource";

interface ExportOptions {
  mode?: "all" | "rows";
  filter_model?: Record<string, unknown>;
  row_group_cols?: Array<{ id: string; field: string; displayName: string }>;
  selected_group_paths?: string[][];
  row_ids?: string[];
  // Ancestor path(s) each checked row_id was under (id-as-string key). Scopes export to selected
  // branches instead of re-deriving non-exclusive status_label scopes (Generate#row_id_group_paths).
  leaf_group_paths?: Record<string, string[][]>;
  search_text?: string;
  // When true, the server emits a lean pivot summary instead of the regular grouped export.
  pivot_mode?: boolean;
  // Whether to export basic or advanced columns — defaults server-side to true (advanced) for
  // callers that don't pass it, so this must be set explicitly to respect the current mode.
  advanced?: boolean;
}

// Deep-link presets for `?grid_filter=` (e.g. ResultHeaderComponent's "Fix" link →
// `grid_filter=undelivered`). Only Advanced-view columns with agSetColumnFilter support this.
const GRID_FILTER_PRESETS: Record<string, Record<string, { filterType: "set"; values: string[] }>> = {
  undelivered: { status_label: { filterType: "set", values: ["Undelivered"] } },
};

// Root dashboard's pinned "Total" row totals fields that only exist as columns in
// Advanced view; harmless for other consumers since their colDefs never define these colIds.
const TURNOUT_FOOTER_COL_IDS = ["turnout", "total_voters", "turnout_pct"];

// Sent instead of '' for blankSentinel columns, since Rails' deep_munge strips bare
// `null` from param arrays. Must match User::Admin::GridData::BLANK_FILTER_KEY.
const BLANK_FILTER_KEY = "__blank__";

// At or below this many DATA columns the grid compresses to fit the viewport (no
// horizontal scroll); above it, natural widths + horizontal scroll.
const COMPRESS_TO_FIT_MAX_DATA_COLS = 5;

// Rendered by the template so the pre-hydration toolbar never paints; dropped once the
// grid exists and the JS-owned chips have settled. See ag_grid_ssrm_component.css.
const BOOTING_CLASS = "ag-grid-component-wrapper--booting";

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "searchInput",
    "confirmExportModal",
    "sortToggle",
    "sortDropdown",
    "filterMenuToggle",
    "filterMenuDropdown",
  ];

  static values = {
    ...AgGridBaseController.values,
    gridDataUrl:         String,
    cacheBlockSize:      { type: Number, default: 100 },
    rowBuffer:           { type: Number, default: 50 },
    pagination:          Boolean,
    searchText:          String,
    // Query key the search box mirrors into the URL; empty (the default) leaves the URL untouched.
    searchParam:         String,
    exportUrl:           String,
    // Present only when Advanced view returns a genuinely different SSRM payload;
    // absent for single-payload grids, whose toggle is a client-side columnDefs swap.
    advancedGridDataUrl: String,
    filterValuesUrl:     String,
    exportMode:          String,
    // Formats the async export backend can actually produce. CSV-only grids must not offer
    // "Excel Export" for a whole-view export — the worker would hand back a .csv.
    exportFormats:       { type: Array, default: ["csv"] },
    alwaysShowHeaderCheckbox: Boolean,
    // Off by default — a vote box row is not an election and cannot satisfy the election-scoped
    // Actions endpoints, so it stays unselectable everywhere. The root dashboard opts in because
    // its Actions menu is selection-driven: without a selectable row there is no way to reach a
    // box's own actions at all. A consumer that opts in MUST gate every election-scoped bulk
    // action off box rows (see SelectionActions' requireAllRowsField: 'is_election').
    selectableVoteBoxRows: Boolean,
    // Admin Users enables this so clicking the row-selection checkbox ALSO opens
    // that row's Actions dropdown; every other consumer keeps checkbox-only-selects.
    checkboxOpensActionsMenu: Boolean,
    // Off by default — updateHeaderCheckbox's filter/search-driven reveal would otherwise
    // override a consumer's static headerCheckbox: false. Opt in only if the checkbox
    // should appear once filtered/searched, rather than always-on via alwaysShowHeaderCheckbox.
    enableHeaderCheckboxOnFilter: Boolean,
    // Admin Users enables this so Basic view never allows multi-cell range
    // highlighting while Advanced view keeps whatever grid_options.cellSelection
    // says; every other consumer keeps cellSelection identical across both tiers.
    disableBasicCellSelection: Boolean,
    // Admin Voters enables this so the right-click menu offers no Export at all
    disableContextMenuExport: Boolean,
    // Admin Users enables this to shrink header title font size; every other
    // consumer keeps ebTheme's default header size.
    compactHeader: Boolean,
    // The root dashboard enables this so its headers read sentence-cased and semi-bold
    // instead of ebTheme's uppercase 700; every other grid keeps the shared styling.
    // Loses to compactHeader if both are set — that one also changes font size.
    plainHeader: Boolean,
    // Empty ([]) for every existing consumer, so no Sort button renders.
    sortOptions:         Array,
    // Empty ([]) for every existing consumer, so no Filter button renders.
    filterOptions:       Array,
    // Empty ({}) for every existing consumer, so nothing is seeded.
    defaultFilterModel:  Object,
    // False for every existing consumer, so rowDragEntireRow/rowDragManaged are never
    // set and no grid-ready event is dispatched.
    enableRowDrag:       Boolean,
    // A `{vote_box_id}`-templated URL. Absent for every existing consumer, so
    // masterDetail/isRowMaster/detailCellRendererParams are never added.
    voteBoxDetailUrl:    String,
    // When true, fit-vs-horizontal-scroll is decided by DATA column count rather than
    // the Basic/Advanced toggle. False for every existing consumer.
    fitColumnsByCount:   Boolean,
    // Height (px) for the pinned bottom "Total" row. 0 (default) means no getRowHeight
    // callback is set; only the root dashboard opts in.
    footerRowHeight:     { type: Number, default: 0 },
    enableDblclickNav:   Boolean,
  };

  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly hasSearchInputTarget: boolean;
  declare readonly confirmExportModalTarget: HTMLElement;
  declare readonly hasConfirmExportModalTarget: boolean;
  declare readonly sortToggleTarget: HTMLButtonElement;
  declare readonly hasSortToggleTarget: boolean;
  declare readonly sortDropdownTarget: HTMLElement;
  declare readonly hasSortDropdownTarget: boolean;
  declare readonly filterMenuToggleTarget: HTMLButtonElement;
  declare readonly hasFilterMenuToggleTarget: boolean;
  declare readonly filterMenuDropdownTarget: HTMLElement;
  declare readonly hasFilterMenuDropdownTarget: boolean;

  declare readonly gridDataUrlValue: string;
  declare readonly cacheBlockSizeValue: number;
  declare readonly rowBufferValue: number;
  declare readonly paginationValue: boolean;
  declare searchTextValue: string;
  declare readonly searchParamValue: string;
  declare readonly exportUrlValue: string;
  declare readonly advancedGridDataUrlValue: string;
  declare readonly hasAdvancedGridDataUrlValue: boolean;
  declare readonly filterValuesUrlValue: string;
  declare readonly exportModeValue: string;
  declare readonly exportFormatsValue: string[];
  declare readonly alwaysShowHeaderCheckboxValue: boolean;
  declare readonly selectableVoteBoxRowsValue: boolean;
  declare readonly checkboxOpensActionsMenuValue: boolean;
  declare readonly enableHeaderCheckboxOnFilterValue: boolean;
  declare readonly disableBasicCellSelectionValue: boolean;
  declare readonly disableContextMenuExportValue: boolean;
  declare readonly compactHeaderValue: boolean;
  declare readonly plainHeaderValue: boolean;
  declare readonly sortOptionsValue: Array<{
    label: string;
    col_id: string;
    default_dir?: "asc" | "desc";
  }>;
  declare readonly filterOptionsValue: Array<{
    label: string;
    col_id: string;
    values?: string[];
    async?: boolean;
    type?: "set" | "date_range";
    modes?: Array<{ label: string; col_id: string }>;
  }>;
  declare readonly defaultFilterModelValue: Record<string, unknown>;
  declare readonly enableRowDragValue: boolean;
  declare readonly voteBoxDetailUrlValue: string;
  declare readonly hasVoteBoxDetailUrlValue: boolean;
  declare readonly fitColumnsByCountValue: boolean;
  declare readonly enableDblclickNavValue: boolean;
  declare readonly footerRowHeightValue: number;

  // A box row (`data.row_type === 1`) isn't an election and can't satisfy the election-scoped
  // Actions endpoints, so it is unselectable unless the consumer opts in via
  // selectableVoteBoxRows — and opting in means gating those bulk actions off box rows.
  private readonly voteBoxAwareRowSelectableCallback = (node: IRowNode): boolean =>
    this.rowSelectableCallback(node) &&
    (this.selectableVoteBoxRowsValue ||
      (node.data as Record<string, unknown> | undefined)?.row_type !== 1);

  // True while the Advanced Vote Boxes side panel is showing; read live by the SSRM
  // datasource so requests while open carry excludeVoteBoxes: true.
  private voteBoxesHidden = false;

  // Set when a deep-linked filter preset forces advanced, so restoring a saved mode cannot undo it.
  private modeForcedByDeepLink = false;

  // Cached so switchToBasic/switchToAdvanced can re-evaluate pinnedBottomRowData on a
  // client-side columnDefs swap (root dashboard never re-fetches on toggle).
  private lastFooterRowData: unknown[] = [];

  private sideBarConfig: false | SideBarDef = false;
  // Only bound when enableRowDragValue is true, so null (no-op) otherwise.
  private requestGridApiHandler: (() => void) | null = null;
  private pendingExportFormat: "csv" | "xlsx" | null = null;
  private sortOutsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private filterMenuOutsideClickHandler: ((e: MouseEvent) => void) | null = null;
  // Keyed by colId, so the Filter dropdown fetches each async facet at most once
  // per grid connection.
  private asyncFilterValuesCache = new Map<string, string[]>();
  // Separates "this admin has used the grid before" from a genuinely first visit.
  private hasPersistedState = false;
  // Baseline for newlyDisplayedColIds; empty at first, so the first pass measures everything.
  private lastDisplayedColIds: string[] = [];
  // Per-value COUNT totals (e.g. Organization -> election count), keyed by colId then
  // value string, rendered as "Name (N)" in the native agSetColumnFilter's Filter List.
  private asyncFilterCountsCache = new Map<string, Record<string, number>>();
  // Additive, opt-in (voter-list importation-overview Gap A) — an external filter UI (e.g.
  // ImportationOverviewComponent's legacy notice_types/groups/errors dropdowns) bridges its
  // current selection in here via `setExternalFilters`. Absent (null) for every OTHER consumer of
  // this shared controller — `ServerSideDatasource` only ever adds `externalFilters` to the wire
  // request when this is non-null (see the getter passed into it below), so behavior for every
  // existing grid is byte-for-byte unchanged.
  private externalFilters: Record<string, unknown> | null = null;

  connect() {
    super.connect(); // license key + outside click handler

    if (!this.hasGridTarget) {
      // Nothing to build, so keeping the toolbar hidden would strand it forever.
      this.element.classList.remove(BOOTING_CLASS);
      return;
    }

    // Before the datasource is built, so block 0 already carries the term.
    this.seedSearchFromUrl();

    const hasAdvanced = this.advancedColumnDefsValue.length > 0;
    const forcedFilterPreset = hasAdvanced ? GRID_FILTER_PRESETS[this.initialGridFilterParam ?? ""] : undefined;

    // A deep-linked filter preset implies advanced. The saved mode is applied later, in
    // loadGridState — the datasource is not attached until that resolves, so restoring advanced
    // there still costs exactly one data request.
    if (hasAdvanced && forcedFilterPreset) {
      this.isBasicMode = false;
      this.modeForcedByDeepLink = true;
    }

    this.sideBarConfig = this.buildSideBarConfig(hasAdvanced) as false | SideBarDef;


    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};

    const options: GridOptions = {
      theme: this.resolveTheme(),
      localeText: ebLocaleText,
      rowModelType: "serverSide",
      cacheBlockSize: this.cacheBlockSizeValue,
      // Page size matches the SSRM cache block size. Omitted (not false) when opted
      // out, so infinite-scroll consumers are unchanged.
      ...(this.paginationValue ? { pagination: true, paginationPageSize: this.cacheBlockSizeValue } : {}),
      enableFilterHandlers: true,
      rowBuffer: this.rowBufferValue,
      animateRows: false,
      rowHeight: 56,
      // Only set for a shorter Totals row — a getRowHeight callback switches AG Grid to
      // per-node height resolution, so grids that don't need it keep the fast path.
      ...(this.footerRowHeightValue > 0
        ? {
            // Returning undefined for every other node falls through to AG Grid's own
            // resolution order, so master-detail and multi-line status cells are untouched.
            getRowHeight: (params: { node: IRowNode }) =>
              (params.node.rowPinned === "bottom" ? this.footerRowHeightValue : undefined) as number | undefined,
          }
        : {}),
      getChildCount: (data) => (data as Record<string, unknown>)?.childCount as number,
      getRowId: (params) => {
        if (params.data.id != null) {
          // Status labels overlap (e.g. Opened is also Not Voted), so scope leaf ids by
          // parent path to avoid id collisions across branches.
          const parentPrefix = (params.parentKeys ?? []).join("/");
          return parentPrefix ? `${parentPrefix}::${params.data.id}` : String(params.data.id);
        }
        const groupKey = Object.entries(params.data as Record<string, unknown>)
          .map(([k, v]) => `${k}:${String(v)}`)
          .join("|");
        const parentPrefix = (params.parentKeys ?? []).join("/");
        return `grp_${params.level}_${parentPrefix}_${groupKey}`;
      },
      getContextMenuItems: (params) => {
        const items: (DefaultMenuItem | MenuItemDef)[] = (params.defaultItems ?? []).filter((item) => item !== "export");
        if (this.disableContextMenuExportValue) return items;
        const subMenu: MenuItemDef[] = [
          { name: "CSV Export", icon: '<span class="ag-icon ag-icon-csv"></span>', action: () => this.exportCsv() },
        ];
        // Offered only when the whole-view export can genuinely produce xlsx, or when a cell range
        // is selected — that path is built client-side by AG Grid and is always a real xlsx.
        if (this.supportsXlsxExport() || this.hasActiveCellRange()) {
          subMenu.push({ name: "Excel Export", icon: '<span class="ag-icon ag-icon-excel"></span>', action: () => this.exportXlsx() });
        }
        items.push({
          name: "Export",
          icon: '<span class="ag-icon ag-icon-save"></span>',
          subMenu,
        });
        return items;
      },
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        const data = params.node?.data as Record<string, unknown> | undefined;
        return resolveDisplayExportValue(params.column.getColDef(), data) ?? formatCellValue(params);
      },
      getMainMenuItems: (params) => {
        const items = params.column?.getColDef().enableRowGroup
          ? params.defaultItems
          : params.defaultItems.filter((item) => item !== "rowGroup");
        return this.withPivotAwareResetColumns({ ...params, defaultItems: items });
      },
      onFirstDataRendered: () => {
        this.autoSizeNonFlexColumns();
        this.prefetchVisibleRowActions();
      },
      onModelUpdated: () => {
        if (this.pendingAutoSize) {
          // Wait until actual row data is present — SSRM fires onModelUpdated
          // immediately on datasource reset (loading state) before blocks load.
          const firstRow = this.gridApi?.getDisplayedRowAtIndex(0);
          if (firstRow?.data !== undefined) {
            this.pendingAutoSize = false;
            this.autoSizeNonFlexColumns();
          }
        }
        // Select-all grows as further blocks load on scroll, rather than staying
        // frozen at the first block.
        if (this.isSelectAllActive()) {
          this.handleSelectionChanged();
        }
        // No-ops unless rowActionsUrl is wired, and skips already-cached rows.
        this.prefetchVisibleRowActions();
      },
      onColumnRowGroupChanged: () => {
        this.pendingAutoSize = true;
        this.updateHeaderCheckbox();
        this.handleSelectionChanged();
        this.syncClearFiltersButton();
      },
      onFilterChanged: () => {
        this.updateHeaderCheckbox();
        this.syncClearFiltersButton();
        this.resetRowActionsCache();
      },
      onCellSelectionChanged: (event: CellSelectionChangedEvent) => {
        if (!event.finished) return;
        const ranges = this.gridApi?.getCellRanges();
        if (!ranges?.length) return;
        for (const range of ranges) {
          if (!range.startRow || !range.endRow) continue;
          const hasCheckboxCol = range.columns.some((c) => c.getColId() === "ag-Grid-SelectionColumn");
          if (!hasCheckboxCol) continue;
          const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
          const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);
          const firstNode = this.gridApi?.getDisplayedRowAtIndex(start);
          const select = !firstNode?.isSelected();
          for (let i = start; i <= end; i++) {
            this.gridApi?.getDisplayedRowAtIndex(i)?.setSelected(select);
          }
        }
      },
      onSelectionChanged: () => {
        this.handleSelectionChanged();
      },
      // NOT onCellClicked: AG-Grid's checkbox handler stops propagation before it fires;
      // onRowSelected's `source` distinguishes "checkboxSelected" from other paths.
      onRowSelected: (event: RowSelectedEvent) => {
        if (!this.checkboxOpensActionsMenuValue) return;
        if (event.source !== "checkboxSelected") return;
        this.openRowActionsMenu(event.node);
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        this.handleCellDoubleClicked(event);
      },
      onStateUpdated: (event: StateUpdatedEvent) => {
        const LAYOUT_SOURCES = new Set([
          "columnSizing", "columnOrder", "columnPinning", "columnVisibility",
          "columnGroup", "sort", "filter", "rowGroup", "pivot", "aggregation", "sideBar",
        ]);
        const AUTOSIZE_SOURCES = new Set(["columnVisibility", "columnPinning"]);
        if (event.sources.some((s) => LAYOUT_SOURCES.has(s))) {
          this.saveGridState();
        }
        if (event.sources.some((s) => AUTOSIZE_SOURCES.has(s))) {
          // Measuring every column rather than the new ones stalls a wide grid for hundreds of ms.
          this.autoSizeNonFlexColumns(this.newlyDisplayedColIds());
        }
        if (event.sources.some((s) => s === "sort" || s === "pivot")) {
          this.syncClearFiltersButton();
        }
      },
      onColumnPivotModeChanged: (_event: ColumnPivotModeChangedEvent) => {
        this.syncCountColumnForPivotMode();
      },
      suppressAggFuncInHeader: this.suppressAggFuncInHeader,
      // Whole-row drag, unmanaged — the row is only ever consumed by an EXTERNAL
      // drop zone (gridApi.addRowDropZone), never reordered by the grid itself.
      ...(this.enableRowDragValue ? { rowDragEntireRow: false, rowDragManaged: false } : {}),
      // Advanced-view master-detail: a box row (`data.row_type === 1`) expands to lazily
      // fetch its child elections. Absent for every existing SSRM consumer.
      ...(this.hasVoteBoxDetailUrlValue
        ? {
            masterDetail: !this.isBasicMode,
            detailRowAutoHeight: true,
            isRowMaster: (data: Record<string, unknown>) => data?.row_type === 1,
            detailCellRendererParams: {
              // Detail rows are always elections, so the detail grid reuses the EXACT SAME
              // Basic election colDefs this grid was given.
              detailGridOptions: {
                theme: this.resolveTheme(),
                columnDefs: this.detailColumnDefs(),
                defaultColDef: { resizable: true, suppressHeaderMenuButton: true },
                cellSelection: (this.gridOptionsValue as Record<string, unknown>).cellSelection === true,
              },
              getDetailRowData: (params: GetDetailRowDataParams) => {
                const entityId = (params.data as Record<string, unknown> | undefined)?.entity_id as
                  | string
                  | number
                  | undefined
                  | null;
                if (entityId == null || !this.voteBoxDetailUrlValue) {
                  params.successCallback([]);
                  return;
                }
                const url = this.voteBoxDetailUrlValue.replace("{vote_box_id}", String(entityId));
                fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
                  .then((response) => (response.ok ? response.json() : { rows: [] }))
                  .then((data: { rows?: unknown[] }) => params.successCallback(data.rows ?? []))
                  .catch(() => params.successCallback([]));
              },
            },
          }
        : {}),
      ...this.gridOptionsValue,
      // The pinned "Total" footer row's label renders in the wide Name column so it
      // shows in full instead of truncating in the narrow pinned checkbox column.
      columnDefs: this.applyRowDragColDef(
        this.withActionsValueFormatter(
          applyErrorFieldCellClassRules(
            this.hydrateColDefs(
              this.isBasicMode ? this.basicColDefs(this.columnDefsValue) : this.advancedColumnDefsValue
            )
          )
        )
      ),
      defaultColDef: this.isBasicMode
        ? { filterParams: ebFilterParams, ...baseColDef, suppressHeaderMenuButton: true, ...this.pivotHeaderQuirkColDefOverrides }
        : { filterParams: ebFilterParams, ...baseColDef, ...this.pivotHeaderQuirkColDefOverrides },
      suppressDragLeaveHidesColumns: this.isBasicMode,
      // Grid-level suppress, unlike per-column suppressMovable, blocks the drag-follow visual too.
      suppressMovableColumns: this.isBasicMode,
      cellSelection: this.buildCellSelection(),
      enableCellTextSelection: this.buildCellTextSelection(),
      autoGroupColumnDef: {
        minWidth: 220,
        ...this.pivotHeaderQuirkColDefOverrides,
      },
      rowSelection: {
        mode: "multiRow",
        checkboxes: true,
        headerCheckbox: this.alwaysShowHeaderCheckboxValue,
        ...(this.gridOptionsValue.rowSelection as Record<string, unknown> ?? {}),
        isRowSelectable: this.voteBoxAwareRowSelectableCallback,
      },
      components: {
        statusCellRenderer: StatusCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
        dropdownActionsCellRenderer: DropdownActionsCellRenderer,
        linkCellRenderer: LinkCellRenderer,
        postLinkCellRenderer: PostLinkCellRenderer,
        multilineTextCellRenderer: MultilineTextCellRenderer,
        maskedTextCellRenderer: MaskedTextCellRenderer,
        iconBadgeCellRenderer: IconBadgeCellRenderer,
        toggleCheckboxCellRenderer: ToggleCheckboxCellRenderer,
        selectedRowCountStatusPanel: SelectedRowCountStatusPanel,
        // Only the root dashboard's Election Name column references this key.
        electionNameCellRenderer: ElectionNameCellRenderer,
        // Only the root dashboard's Status column references this composite filter.
        statusTestFilter: StatusTestFilter,
        ...((this.gridOptionsValue.components as Record<string, unknown>) ?? {}),
      },
      // Only invoked in Client-Side Row Model (hybrid mode); in SSRM the model rides
      // to the server and this is never called.
      filterHandlers: {
        statusTestFilter: statusTestFilterHandler,
      },
    };

    this.gridApi = createGrid(this.gridTarget, options);
    this.syncCountColumnForPivotMode();
    this.setupToolPanelHeaders();

    if (this.enableRowDragValue) {
      this.setupGridReadyForExternalDropZones();
      // Scopes the drag-handle CSS to ONLY this grid; panel starts closed, so the
      // drag-handle column reserves no horizontal space until it opens.
      this.gridTarget.classList.add("ssrm-row-drag");
      this.gridTarget.classList.toggle("ssrm-row-drag--panel-open", this.voteBoxesHidden);
    }

    this.setupFollowSelection();

    if (!this.isBasicMode) {
      this.setActiveToggle("advanced");
      if (this.sideBarConfig) this.gridApi.setGridOption("sideBar", this.sideBarConfig);
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.style.display = "";
      if (this.hasFilterToggleTarget) this.filterToggleTarget.style.display = "";
    }

    // Everything above is synchronous, so the toolbar's first paint is already its settled
    // one -- no chips popping in after the fact. Rows still stream in behind the loading
    // overlay; this only gates the chrome.
    this.element.classList.remove(BOOTING_CLASS);

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      const exportWrapper = this.hasExportToggleTarget
        ? this.exportToggleTarget.closest(".ag-grid-component-export-wrapper")
        : null;
      if (exportWrapper?.contains(target)) return;
      const sortWrapper = this.hasSortToggleTarget
        ? this.sortToggleTarget.closest(".ag-grid-component-sort-wrapper")
        : null;
      if (sortWrapper?.contains(target)) return;
      const filterWrapper = this.hasFilterMenuToggleTarget
        ? this.filterMenuToggleTarget.closest(".ag-grid-component-filter-wrapper")
        : null;
      if (filterWrapper?.contains(target)) return;
      this.gridApi?.clearCellSelection();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);

    if (this.hasSortDropdownTarget) {
      this.sortOutsideClickHandler = (e: MouseEvent) => {
        if (this.sortDropdownTarget.hidden) return;
        if (
          !this.sortToggleTarget.contains(e.target as Node) &&
          !this.sortDropdownTarget.contains(e.target as Node)
        ) {
          this.closeSortDropdown();
        }
      };
      document.addEventListener("click", this.sortOutsideClickHandler);
    }

    if (this.hasFilterMenuDropdownTarget) {
      this.filterMenuOutsideClickHandler = (e: MouseEvent) => {
        if (this.filterMenuDropdownTarget.hidden) return;
        if (
          !this.filterMenuToggleTarget.contains(e.target as Node) &&
          !this.filterMenuDropdownTarget.contains(e.target as Node)
        ) {
          this.closeFilterMenuDropdown();
        }
      };
      document.addEventListener("click", this.filterMenuOutsideClickHandler);
    }

    const isAutoHeight = this.gridOptionsValue?.domLayout === "autoHeight";
    if (!isAutoHeight) {
      this.initDynamicHeight();
    }

    const gridStateLoaded = this.loadGridState();
    if (forcedFilterPreset) {
      // Apply after any saved grid state has loaded so the deep-link filter always wins over a
      // previously persisted filter model.
      void gridStateLoaded.then(() => this.gridApi?.setFilterModel(forcedFilterPreset));
    }
    // Before syncClearFiltersButton below, or Clear stays hidden while the defaults are active.
    void gridStateLoaded.then(() => this.applyDefaultFilterModelIfUnset());
    void gridStateLoaded.then(() => this.applyDefaultSortIfUnset());
    void gridStateLoaded.then(() => this.syncClearFiltersButton());
    // Attach the SSRM datasource LAST, after state restore/filter preset/default sort all ran, so
    // the initial block-0 load fires once with the correct sort/filter. It is also built here rather
    // than up front because loadGridState may have restored advanced mode, which changes which data
    // URL the datasource must point at.
    void gridStateLoaded.then(() => this.gridApi?.setGridOption("serverSideDatasource", this.buildDatasource()));
  }

  // Grids with a separate advanced payload start on the matching URL when restoring
  // Advanced mode; single-payload grids always use gridDataUrl and toggle client-side.
  private buildDatasource(): ServerSideDatasource {
    const url =
      !this.isBasicMode && this.hasAdvancedGridDataUrlValue && this.advancedGridDataUrlValue
        ? this.advancedGridDataUrlValue
        : this.gridDataUrlValue;
    return new ServerSideDatasource(
      url,
      () => this.searchTextValue,
      (rows) => this.applyFooterRow(rows),
      () => this.voteBoxesHidden,
      () => this.externalFilters
    );
  }

  // A persisted or user-chosen sort always wins (guarded by currentSortState).
  private applyDefaultSortIfUnset() {
    if (!this.gridApi || this.currentSortState()) return;
    const def = this.sortOptionsValue.find((o) => o.default_dir);
    if (!def) return;
    this.gridApi.applyColumnState({
      state: [{ colId: def.col_id, sort: def.default_dir ?? "asc", sortIndex: 0 }],
      defaultState: { sort: null, sortIndex: null },
    });
  }

  // First visit only: with saved state an empty model is a deliberate clear, and re-seeding would undo it.
  private applyDefaultFilterModelIfUnset() {
    if (!this.gridApi || this.hasPersistedState) return;
    const seed = this.defaultFilterModelValue;
    if (!seed || Object.keys(seed).length === 0) return;
    if (Object.keys(this.gridApi.getFilterModel()).length > 0) return;

    this.gridApi.setFilterModel(seed);
  }

  // Unconditional, unlike the guarded version above: a tier switch has just cleared the model, so
  // the incoming tier should open on the seeded default rather than unfiltered.
  private applyDefaultFilterModel() {
    if (!this.gridApi) return;
    const seed = this.defaultFilterModelValue;
    if (!seed || Object.keys(seed).length === 0) return;

    this.gridApi.setFilterModel(seed);
  }

  private get initialGridFilterParam(): string | null {
    return new URLSearchParams(location.search).get("grid_filter");
  }

  // True once at least one turnout footer colId is a currently visible column.
  // Basic view's colDefs never define them, so this is false there.
  private hasVisibleTurnoutFooterColumn(): boolean {
    return TURNOUT_FOOTER_COL_IDS.some((colId) => this.gridApi?.getColumn(colId)?.isVisible() ?? false);
  }

  // True only when one of this grid's OWN colDefs is a turnout footer column — other
  // SSRM consumers footer unrelated columns and must not be gated on this at all.
  private declaresTurnoutFooterColumn(): boolean {
    const declared = [...(this.columnDefsValue ?? []), ...(this.advancedColumnDefsValue ?? [])];

    return declared.some((col) =>
      TURNOUT_FOOTER_COL_IDS.includes(String(col.colId ?? col.field ?? ""))
    );
  }

  // A turnout footer shows only while at least one of its columns is visible
  // (Advanced); any other consumer's footer passes through untouched.
  private resolveFooterRows(rows: unknown[]): unknown[] {
    if (!this.declaresTurnoutFooterColumn()) return rows;

    return this.hasVisibleTurnoutFooterColumn() ? rows : [];
  }

  // Caches the row so a later Basic/Advanced toggle (which never re-fetches) can
  // re-evaluate visibility without a fresh footerRow.
  private applyFooterRow(rows: unknown[]) {
    this.lastFooterRowData = rows;
    this.gridApi?.setGridOption("pinnedBottomRowData", this.resolveFooterRows(rows));
  }

  // A request/response handshake over `document` so an external consumer (e.g. the
  // Vote Boxes panel) can obtain gridApi regardless of connect order.
  private setupGridReadyForExternalDropZones() {
    this.dispatchGridReady();
    this.requestGridApiHandler = () => this.dispatchGridReady();
    document.addEventListener("votes:dashboard:request-grid-api", this.requestGridApiHandler);
  }

  private dispatchGridReady() {
    document.dispatchEvent(
      new CustomEvent("votes:dashboard:grid-ready", { detail: { gridApi: this.gridApi } })
    );
  }

  disconnect() {
    if (this.sortOutsideClickHandler) {
      document.removeEventListener("click", this.sortOutsideClickHandler);
      this.sortOutsideClickHandler = null;
    }
    if (this.filterMenuOutsideClickHandler) {
      document.removeEventListener("click", this.filterMenuOutsideClickHandler);
      this.filterMenuOutsideClickHandler = null;
    }
    if (this.requestGridApiHandler) {
      document.removeEventListener("votes:dashboard:request-grid-api", this.requestGridApiHandler);
      this.requestGridApiHandler = null;
    }
    super.disconnect();
  }

  onSearchChanged() {
    if (!this.gridApi || !this.hasSearchInputTarget) return;
    this.searchTextValue = this.searchInputTarget.value;
    this.updateHeaderCheckbox();
    this.syncClearFiltersButton();
    this.syncSearchToUrl();
    this.refreshSearchDebounced();
  }

  // replaceState, not pushState: an entry per keystroke would make Back walk every intermediate
  // term. history.state is passed through so Turbo keeps its restoration identifier.
  private syncSearchToUrl(): void {
    if (!this.searchParamValue) return;
    const url = new URL(window.location.href);
    const term = this.searchTextValue.trim();
    if (term) {
      url.searchParams.set(this.searchParamValue, term);
    } else {
      url.searchParams.delete(this.searchParamValue);
    }
    window.history.replaceState(window.history.state, "", url);
  }

  // The URL wins over the server-rendered initial_search: a Turbo restoration visit replays a
  // cached snapshot whose input carries no typed value.
  private seedSearchFromUrl(): void {
    if (!this.searchParamValue) return;
    const term = new URLSearchParams(location.search).get(this.searchParamValue)?.trim() ?? "";
    this.searchTextValue = term;
    if (this.hasSearchInputTarget) this.searchInputTarget.value = term;
  }

  // Debounced 375 ms so a full server round-trip + block purge fires once per pause in
  // typing, not once per keystroke — same pattern as saveGridState below.
  private refreshSearchDebounced = (() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        this.resetRowActionsCache();
        this.gridApi?.refreshServerSide({ purge: true });
      }, 375);
    };
  })();

  // Sort toolbar control — drives AG Grid's real column-state sort API
  // (applyColumnState) rather than a bespoke client-side sort-model override.
  toggleSortDropdown() {
    if (!this.hasSortDropdownTarget) return;
    const isOpen = !this.sortDropdownTarget.hidden;
    this.gridApi?.closeToolPanel();
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    this.closeExportDropdown();
    if (isOpen) {
      this.closeSortDropdown();
    } else {
      this.renderSortOptions();
      this.sortDropdownTarget.hidden = false;
      if (this.hasSortToggleTarget) this.sortToggleTarget.setAttribute("aria-pressed", "true");
    }
  }

  private closeSortDropdown() {
    if (!this.hasSortDropdownTarget) return;
    this.sortDropdownTarget.hidden = true;
    if (this.hasSortToggleTarget) this.sortToggleTarget.setAttribute("aria-pressed", "false");
  }

  private currentSortState(): { colId: string; sort: "asc" | "desc" } | null {
    const state = this.gridApi?.getColumnState() ?? [];
    const sorted = state.find((s) => s.sort != null);
    return sorted ? { colId: sorted.colId, sort: sorted.sort as "asc" | "desc" } : null;
  }

  private renderSortOptions() {
    if (!this.hasSortDropdownTarget) return;
    const dropdown = this.sortDropdownTarget;
    dropdown.innerHTML = "";
    const active = this.currentSortState();

    // Each field has explicit Asc/Desc buttons, so field + direction is a single
    // one-click choice rather than a hidden click-again-to-reverse toggle.
    this.sortOptionsValue.forEach((option) => {
      const row = document.createElement("div");
      row.className = "ag-grid-component-sort-row";

      const labelSpan = document.createElement("span");
      labelSpan.className = "ag-grid-component-sort-row__label";
      labelSpan.textContent = option.label;
      row.appendChild(labelSpan);

      const dirs = document.createElement("span");
      dirs.className = "ag-grid-component-sort-row__dirs";
      (["asc", "desc"] as const).forEach((direction) => {
        const btn = document.createElement("button");
        btn.type = "button";
        const isActive = active?.colId === option.col_id && active.sort === direction;
        btn.className = isActive
          ? "ag-grid-component-sort-dir ag-grid-component-sort-dir--active"
          : "ag-grid-component-sort-dir";
        btn.textContent = direction === "asc" ? "↑" : "↓";
        btn.setAttribute("aria-pressed", String(isActive));
        btn.setAttribute(
          "aria-label",
          `Sort by ${option.label} ${direction === "asc" ? "ascending" : "descending"}`,
        );
        btn.addEventListener("click", () => this.selectSort(option.col_id, direction));
        dirs.appendChild(btn);
      });
      row.appendChild(dirs);

      dropdown.appendChild(row);
    });

    const clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.className = "ag-grid-component-sort-option";
    clearBtn.textContent = "Clear sort";
    clearBtn.addEventListener("click", () => this.clearSort());
    dropdown.appendChild(clearBtn);
  }

  private selectSort(colId: string, direction: "asc" | "desc") {
    if (!this.gridApi) return;
    this.gridApi.applyColumnState({
      state: [{ colId, sort: direction, sortIndex: 0 }],
      defaultState: { sort: null, sortIndex: null },
    });
    // Keep the dropdown open so the chosen direction button shows its active state
    // immediately; the outside-click handler still dismisses the menu.
    this.renderSortOptions();
  }

  private clearSort() {
    this.gridApi?.applyColumnState({ defaultState: { sort: null, sortIndex: null } });
    this.renderSortOptions();
  }

  // Filter toolbar control — drives AG Grid's real filterModel (setFilterModel), so
  // it shares one filterModel with per-column header funnels and stays in lockstep.
  toggleFilterMenu() {
    if (!this.hasFilterMenuDropdownTarget) return;
    const isOpen = !this.filterMenuDropdownTarget.hidden;
    this.gridApi?.closeToolPanel();
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    this.closeExportDropdown();
    this.closeSortDropdown();
    if (isOpen) {
      this.closeFilterMenuDropdown();
    } else {
      this.renderFilterMenu();
      this.filterMenuDropdownTarget.hidden = false;
      if (this.hasFilterMenuToggleTarget) this.filterMenuToggleTarget.setAttribute("aria-pressed", "true");
    }
  }

  private closeFilterMenuDropdown() {
    if (!this.hasFilterMenuDropdownTarget) return;
    this.filterMenuDropdownTarget.hidden = true;
    if (this.hasFilterMenuToggleTarget) this.filterMenuToggleTarget.setAttribute("aria-pressed", "false");
  }

  private activeFilterValues(colId: string): string[] {
    const model = (this.gridApi?.getFilterModel() ?? {}) as Record<
      string,
      { values?: string[] } | undefined
    >;
    return model[colId]?.values ?? [];
  }

  private renderFilterMenu() {
    if (!this.hasFilterMenuDropdownTarget) return;
    const dropdown = this.filterMenuDropdownTarget;
    dropdown.innerHTML = "";

    this.filterOptionsValue.forEach((option) => dropdown.appendChild(this.buildFilterSection(option)));

    const clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.className = "ag-grid-component-sort-option";
    clearBtn.textContent = "Clear all filters";
    clearBtn.addEventListener("click", () => this.clearFilterMenuSelections());
    dropdown.appendChild(clearBtn);
  }

  private buildFilterSection(option: {
    label: string;
    col_id: string;
    values?: string[];
    async?: boolean;
    type?: "set" | "date_range";
    modes?: Array<{ label: string; col_id: string }>;
  }): HTMLElement {
    if (option.type === "date_range") return this.buildDateRangeSection(option);

    const section = document.createElement("details");
    section.className = "ag-grid-component-filter-section";

    const summary = document.createElement("summary");
    summary.className = "ag-grid-component-filter-section__label";
    const labelSpan = document.createElement("span");
    labelSpan.textContent = option.label;
    summary.appendChild(labelSpan);
    const badge = document.createElement("span");
    badge.className = "ag-grid-component-filter-section__count";
    section.appendChild(summary);

    const body = document.createElement("div");
    body.className = "ag-grid-component-filter-section__body";
    section.appendChild(body);

    const updateBadge = () => {
      const count = this.activeFilterValues(option.col_id).length;
      badge.textContent = count > 0 ? String(count) : "";
      if (count > 0) {
        summary.appendChild(badge);
      } else if (badge.parentElement) {
        badge.remove();
      }
    };
    updateBadge();

    if (option.async) {
      let loaded = false;
      section.addEventListener("toggle", () => {
        if (!section.open || loaded) return;
        loaded = true;
        body.textContent = "Loading…";
        void this.fetchAsyncFilterValues(option.col_id).then((values) => {
          body.innerHTML = "";
          this.appendFilterSearch(body);
          this.appendFilterCheckboxes(body, option.col_id, values, updateBadge);
        });
      });
    } else {
      this.appendFilterCheckboxes(body, option.col_id, option.values ?? [], updateBadge);
    }

    return section;
  }

  private buildDateRangeSection(option: {
    label: string;
    col_id: string;
    modes?: Array<{ label: string; col_id: string }>;
  }): HTMLElement {
    const modes = option.modes ?? [{ label: option.label, col_id: option.col_id }];
    const colIds = modes.map((mode) => mode.col_id);

    const section = document.createElement("details");
    section.className = "ag-grid-component-filter-section";

    const summary = document.createElement("summary");
    summary.className = "ag-grid-component-filter-section__label";
    const labelSpan = document.createElement("span");
    labelSpan.textContent = option.label;
    summary.appendChild(labelSpan);
    const badge = document.createElement("span");
    badge.className = "ag-grid-component-filter-section__count";
    section.appendChild(summary);

    const body = document.createElement("div");
    body.className = "ag-grid-component-filter-section__body ag-grid-component-filter-daterange";
    section.appendChild(body);

    const active = modes.find((mode) => this.activeDateFilter(mode.col_id));
    const range = active ? this.activeDateFilter(active.col_id) : null;

    const modeSelect = document.createElement("select");
    modeSelect.className = "ag-grid-component-filter-daterange__mode";
    modeSelect.setAttribute("aria-label", `${option.label} field`);
    modes.forEach((mode) => {
      const opt = document.createElement("option");
      opt.value = mode.col_id;
      opt.textContent = mode.label;
      modeSelect.appendChild(opt);
    });
    modeSelect.value = active?.col_id ?? modes[0].col_id;

    const fromInput = this.buildDateInput(range?.dateFrom);
    const toInput = this.buildDateInput(range?.dateTo);

    const updateBadge = () => {
      const applied = colIds.some((colId) => this.activeDateFilter(colId));
      badge.textContent = applied ? "1" : "";
      if (applied) {
        summary.appendChild(badge);
      } else if (badge.parentElement) {
        badge.remove();
      }
    };
    updateBadge();

    const apply = () => {
      this.applyDateRangeFilter(modeSelect.value, colIds, fromInput.value, toInput.value);
      [fromInput, toInput].forEach((input) => this.markDateInputEmptiness(input));
      updateBadge();
    };
    [modeSelect, fromInput, toInput].forEach((el) => el.addEventListener("change", apply));

    body.appendChild(modeSelect);
    body.appendChild(this.buildDateField("From", fromInput));
    body.appendChild(this.buildDateField("To", toInput));
    return section;
  }

  private buildDateInput(value?: string): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "date";
    input.className = "ag-grid-component-filter-daterange__date";
    input.value = value?.split(" ")[0] ?? "";
    this.markDateInputEmptiness(input);
    return input;
  }

  private markDateInputEmptiness(input: HTMLInputElement) {
    input.classList.toggle("ag-grid-component-filter-daterange__date--empty", input.value === "");
  }

  private buildDateField(caption: string, input: HTMLInputElement): HTMLLabelElement {
    const field = document.createElement("label");
    field.className = "ag-grid-component-filter-daterange__field";
    const label = document.createElement("span");
    label.className = "ag-grid-component-filter-daterange__field-label";
    label.textContent = caption;
    field.appendChild(label);
    field.appendChild(input);
    return field;
  }

  private activeDateFilter(colId: string): { dateFrom?: string; dateTo?: string } | null {
    const model = (this.gridApi?.getFilterModel() ?? {}) as Record<
      string,
      { dateFrom?: string; dateTo?: string } | undefined
    >;
    return model[colId] ?? null;
  }

  private applyDateRangeFilter(colId: string, allColIds: string[], from: string, to: string) {
    if (!this.gridApi) return;
    const model = { ...(this.gridApi.getFilterModel() ?? {}) } as Record<string, unknown>;
    allColIds.forEach((id) => delete model[id]);
    if (from && to) {
      model[colId] = {
        filterType: "date",
        type: "inRange",
        dateFrom: `${from} 00:00:00`,
        dateTo: `${to} 00:00:00`,
      };
    }
    this.gridApi.setFilterModel(model);
  }

  private appendFilterSearch(body: HTMLElement) {
    const search = document.createElement("input");
    search.type = "text";
    search.placeholder = "Search";
    search.className = "ag-grid-component-filter-search";
    search.addEventListener("input", () => {
      const term = search.value.trim().toLowerCase();
      body.querySelectorAll<HTMLElement>(".ag-grid-component-filter-option").forEach((row) => {
        const text = row.dataset.value?.toLowerCase() ?? "";
        row.style.display = term === "" || text.includes(term) ? "" : "none";
      });
    });
    body.appendChild(search);
  }

  private appendFilterCheckboxes(
    body: HTMLElement,
    colId: string,
    values: string[],
    onChange: () => void,
  ) {
    const selected = new Set(this.activeFilterValues(colId));
    values.forEach((value) => {
      const row = document.createElement("label");
      row.className = "ag-grid-component-filter-option";
      row.dataset.value = value;
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.value = value;
      checkbox.checked = selected.has(value);
      checkbox.addEventListener("change", () => {
        const checked = Array.from(
          body.querySelectorAll<HTMLInputElement>("input[type=checkbox]"),
        )
          .filter((c) => c.checked)
          .map((c) => c.value);
        this.applyColumnFilter(colId, checked);
        onChange();
      });
      const text = document.createElement("span");
      // Mirror the grid: the Test facet's "Test" value renders as the same yellow
      // badge shown in the Status cell, rather than plain text.
      if (colId === "test" && value === "Test") {
        const badge = document.createElement("span");
        badge.className = "votes--dashboard--election-component__election-test";
        badge.textContent = "Test";
        text.appendChild(badge);
      } else {
        text.textContent = value;
      }
      row.appendChild(checkbox);
      row.appendChild(text);
      body.appendChild(row);
    });
  }

  private applyColumnFilter(colId: string, values: string[]) {
    if (!this.gridApi) return;
    const model = { ...(this.gridApi.getFilterModel() ?? {}) } as Record<string, unknown>;
    if (values.length > 0) {
      model[colId] = { filterType: "set", values };
    } else {
      delete model[colId];
    }
    this.gridApi.setFilterModel(model);
  }

  // Distinct from the toolbar chip's clearAllFilters(), which also resets
  // sort/grouping/search/pivot.
  private clearFilterMenuSelections() {
    this.gridApi?.setFilterModel({});
    this.renderFilterMenu();
  }

  private fetchAsyncFilterValues(colId: string): Promise<string[]> {
    const cached = this.asyncFilterValuesCache.get(colId);
    if (cached) return Promise.resolve(cached);
    if (!this.filterValuesUrlValue) return Promise.resolve([]);

    const url = `${this.filterValuesUrlValue}?column=${encodeURIComponent(colId)}`;
    return fetch(url, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : { values: [] }))
      .then((data: { values?: string[] }) => {
        const values = data.values ?? [];
        this.asyncFilterValuesCache.set(colId, values);
        return values;
      })
      .catch(() => []);
  }

  // Additive, opt-in (voter-list importation-overview Gap A) — public API for an external filter
  // UI to bridge its current selection into this grid's SSRM requests. Callers reach this via
  // `this.application.getControllerForElementAndIdentifier(...)` (see
  // importation_overview_component_controller.js#submit); the grid's own quick-search input
  // (`onSearchChanged` above) is untouched and keeps working independently — whichever sets
  // `searchTextValue` last wins on the next request, same as any other Stimulus Value write.
  //
  // A filters object whose values are all empty arrays (e.g. every dropdown cleared) is treated
  // as "no active external filter" — `null`, not an empty object — so `ServerSideDatasource` omits
  // `externalFilters` from the wire request entirely rather than sending a no-op payload.
  setExternalFilters(filters: Record<string, unknown>): void {
    this.externalFilters = this.hasActiveExternalFilters(filters) ? filters : null;
    if (!this.gridApi) return;
    // No `purge`: keeps the SSRM cache signature consistent with `onSearchChanged`'s own
    // `{ purge: true }` — a filter change always means page-0 reload of the new result set.
    this.gridApi.refreshServerSide({ purge: true });
    this.updateHeaderCheckbox();
  }

  private hasActiveExternalFilters(filters: Record<string, unknown>): boolean {
    return Object.values(filters).some((value) => Array.isArray(value) && value.length > 0);
  }

  switchToBasic() {
    if (!this.gridApi) return;
    this.applyBasicMode();
    this.applyViewColumnDefs(this.gridDataUrlValue, this.basicColDefs(this.columnDefsValue));
    // Re-evaluate (rather than re-fetch) so the pinned "Total" row disappears immediately.
    this.gridApi.setGridOption(
      "pinnedBottomRowData",
      this.resolveFooterRows(this.lastFooterRowData)
    );
    // `mode` is not part of AG Grid's state, so a mode flip alone may not emit onStateUpdated.
    this.saveGridState();
  }

  // Everything the basic view implies except the column defs and datasource, which the caller owns:
  // restoring saved state needs the chrome without triggering a second data fetch.
  private applyBasicMode() {
    if (!this.gridApi) return;
    this.isBasicMode = true;
    this.setActiveToggle("basic");
    this.gridApi.closeToolPanel();
    this.gridApi.setGridOption("sideBar", false);
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "none";
      this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.style.display = "none";
      this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { filterParams: ebFilterParams, ...baseColDef, suppressHeaderMenuButton: true });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", true);
    this.gridApi.setGridOption("suppressMovableColumns", true);
    this.gridApi.clearCellSelection();
    this.gridApi.setGridOption("cellSelection", this.buildCellSelection());
    this.gridApi.setGridOption("enableCellTextSelection", this.buildCellTextSelection());
    // Basic never shows an expand affordance on a box row — turn masterDetail off
    // entirely, then redrawRows per AG Grid's documented pattern.
    if (this.hasVoteBoxDetailUrlValue) {
      this.gridApi.setGridOption("masterDetail", false);
      this.gridApi.redrawRows();
    }
  }

  // Called by the sibling Vote Boxes panel controller when it show/hides — a
  // defensive nudge since AG Grid's own `flex` colDefs already track container width.
  // Skipped when too narrow, where sizeColumnsToFit would undo the measured widths.
  resizeGrid() {
    if (!this.hasRoomToFitColumns()) return;

    this.gridApi?.sizeColumnsToFit();
  }

  // Called by the sibling Vote Boxes panel controller when it show/hides. Stores
  // the flag the datasource reads and purges + refetches only when it changed.
  setVoteBoxesHidden(hidden: boolean) {
    if (this.voteBoxesHidden === hidden) return;
    this.voteBoxesHidden = hidden;
    if (!this.gridApi) return;
    // With the panel closed the row must not be draggable at all, matching the
    // hidden grip.
    if (this.enableRowDragValue) {
      this.gridApi.setGridOption("rowDragEntireRow", hidden);
      // Collapse the drag-handle column's reserved space while the panel is closed.
      this.gridTarget.classList.toggle("ssrm-row-drag--panel-open", hidden);
    }
    this.resetRowActionsCache();
    this.gridApi.refreshServerSide({ purge: true });
  }

  switchToAdvanced() {
    if (!this.gridApi) return;
    this.applyAdvancedMode();
    this.applyViewColumnDefs(this.advancedGridDataUrlValue, this.advancedColumnDefsValue);
    this.gridApi.setGridOption(
      "pinnedBottomRowData",
      this.resolveFooterRows(this.lastFooterRowData)
    );
    this.saveGridState();
  }

  // Advanced-view chrome without the column defs or datasource — see applyBasicMode.
  private applyAdvancedMode() {
    if (!this.gridApi) return;
    this.isBasicMode = false;
    this.setActiveToggle("advanced");
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { filterParams: ebFilterParams, ...baseColDef });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", false);
    this.gridApi.setGridOption("suppressMovableColumns", false);
    this.gridApi.clearCellSelection();
    this.gridApi.setGridOption("cellSelection", this.buildCellSelection());
    this.gridApi.setGridOption("enableCellTextSelection", this.buildCellTextSelection());
    if (this.sideBarConfig) {
      this.gridApi.setGridOption("sideBar", this.sideBarConfig);
    }
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.style.display = "";
    if (this.hasFilterToggleTarget) this.filterToggleTarget.style.display = "";
    if (this.hasVoteBoxDetailUrlValue) {
      this.gridApi.setGridOption("masterDetail", true);
      this.gridApi.redrawRows();
    }
  }

  // The state event reports that visibility changed, not which way — hence the diff.
  private newlyDisplayedColIds(): string[] {
    const displayed = (this.gridApi?.getAllDisplayedColumns() ?? []).map((col) => col.getColId());
    const previous = new Set(this.lastDisplayedColIds);
    this.lastDisplayedColIds = displayed;
    return displayed.filter((colId) => !previous.has(colId));
  }

  exportCsv() {
    this.closeExportDropdown();
    if (!this.gridApi) return;

    const ranges = this.gridApi.getCellRanges();
    if (ranges && this.isActiveRange(ranges)) {
      this.exportSelectedCellsAsCsv(ranges);
      return;
    }

    const selOptions = this.resolveSelectionExportOptions();
    if (selOptions) {
      void this.triggerAsyncExport("csv", selOptions);
      return;
    }

    this.pendingExportFormat = "csv";
    this.openExportConfirmModal();
  }

  exportXlsx() {
    this.closeExportDropdown();
    if (!this.gridApi) return;

    const ranges = this.gridApi.getCellRanges();
    if (ranges && this.isActiveRange(ranges)) {
      this.exportSelectedCellsAsXlsx(ranges);
      return;
    }

    // Backstop for the whole-view paths: a CSV-only backend would return a .csv under an xlsx
    // request, so fall back to CSV explicitly rather than promising a format we can't deliver.
    const format = this.supportsXlsxExport() ? "xlsx" : "csv";

    const selOptions = this.resolveSelectionExportOptions();
    if (selOptions) {
      void this.triggerAsyncExport(format, selOptions);
      return;
    }

    this.pendingExportFormat = format;
    this.openExportConfirmModal();
  }

  private supportsXlsxExport(): boolean {
    return (this.exportFormatsValue ?? []).includes("xlsx");
  }

  private hasActiveCellRange(): boolean {
    if (!this.gridApi) return false;
    const ranges = this.gridApi.getCellRanges();
    return !!ranges && this.isActiveRange(ranges);
  }

  openExportConfirmModal() {
    if (!this.hasConfirmExportModalTarget) return;
    this.confirmExportModalTarget.removeAttribute("hidden");
    document.body.style.overflow = "hidden";
    const cancelBtn = this.confirmExportModalTarget.querySelector<HTMLButtonElement>(
      "[data-action*='cancelExport']"
    );
    if (cancelBtn) setTimeout(() => cancelBtn.focus(), 50);
  }

  closeExportConfirmModal() {
    if (!this.hasConfirmExportModalTarget) return;
    this.confirmExportModalTarget.setAttribute("hidden", "");
    document.body.style.overflow = "";
  }

  backdropClickExportModal(event: Event) {
    if (event.target === this.confirmExportModalTarget) this.closeExportConfirmModal();
  }

  confirmExport() {
    this.closeExportConfirmModal();
    const format = this.pendingExportFormat;
    if (!format || !this.gridApi) return;
    this.pendingExportFormat = null;

    void this.triggerAsyncExport(format, {
      mode: "all",
      filter_model: this.gridApi.getFilterModel() as Record<string, unknown>,
      row_group_cols: this.getRowGroupColDefs(),
      search_text: this.searchTextValue || undefined,
      pivot_mode: this.gridApi.isPivotMode(),
      advanced: !this.isBasicMode,
    });
  }

  cancelExport() {
    this.closeExportConfirmModal();
    this.pendingExportFormat = null;
  }

  private async triggerAsyncExport(format: "csv" | "xlsx", options: ExportOptions = {}) {
    if (!this.exportUrlValue) return;

    // inline_csv downloads a CSV directly instead of the async
    // Turbo-Stream flow below. Non-empty only when explicitly set to `export_mode: :inline_csv`.
    if (this.exportModeValue === "inline_csv") {
      await this.downloadInlineCsv(options);
      return;
    }

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        },
        credentials: "same-origin",
        // Deliberately no `request_id`: that tag is what opts a response into
        // DownloadFileChannel's force-download. `:report_center` exports are collected
        // from /users/reports, so tagging them made the browser download the file the
        // moment the worker finished — on top of the Report Center delivery the confirm
        // modal promised. Immediate-download flows (voter lists, results summaries, the
        // legacy billing Download button) still tag their own requests in
        // download_file_channel.js.
        body: JSON.stringify({ format, ...options }),
      });
      if (response.ok) {
        const html = await response.text();
        if (html.trim()) window.Turbo?.renderStreamMessage(html);
      }
    } catch {
      // Export request failed silently — user can retry
    }
  }

  // `:inline_csv` mode (spec 011 Decision B): re-runs the query with the live filter/sort/search
  // options and streams the response as a CSV download instead of Turbo-Stream HTML.
  private async downloadInlineCsv(options: ExportOptions = {}) {
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "text/csv",
        },
        credentials: "same-origin",
        body: JSON.stringify({ format: "csv", ...options }),
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = this.inlineCsvFilename(response);
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Export request failed silently — user can retry
    }
  }

  private inlineCsvFilename(response: Response): string {
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="?([^";]+)"?/.exec(disposition);
    return match?.[1] ?? "export.csv";
  }

  // advancedGridDataUrl PRESENT: Advanced returns genuinely different rows, so reset
  // state and refetch. ABSENT (root dashboard): pure client-side columnDefs swap.
  private applyViewColumnDefs(gridDataUrl: string, columnDefs: ColDef[]) {
    if (!this.gridApi) return;
    const formatted = this.withActionsValueFormatter(columnDefs);
    if (this.hasAdvancedGridDataUrlValue && this.advancedGridDataUrlValue) {
      // Defs before the reset, and the reset reads them directly: AG Grid keeps the live `hide` for
      // a colId both tiers declare, so reading it back would carry the outgoing tier's over.
      this.applyDatasourceColumnDefs(formatted);
      this.resetGridState(formatted);
      // Before the datasource attaches, so the incoming tier's first block already carries the seed.
      this.applyDefaultFilterModel();
      this.attachDatasource(gridDataUrl);
    } else {
      this.gridApi.setGridOption("columnDefs", this.applyRowDragColDef(this.hydrateColDefs(formatted)));
      this.autoSizeNonFlexColumns();
      this.pendingAutoSize = true;
    }
  }

  // Opt-in: fit-vs-scroll driven by DATA column count rather than the toggle. Must
  // stay opt-in — the results grid's dynamic Basic columns would regress otherwise.
  protected override preserveFlexOnAutoSize(): boolean {
    if (!this.fitColumnsByCountValue) return this.isBasicMode;

    const dataColCount = (this.gridApi?.getAllDisplayedColumns() ?? []).filter(
      (col) => !col.getColId().startsWith("ag-Grid-")
    ).length;
    return dataColCount <= COMPRESS_TO_FIT_MAX_DATA_COLS;
  }

  private applyDatasourceColumnDefs(columnDefs: ColDef[]) {
    if (!this.gridApi) return;
    this.resetRowActionsCache();
    this.gridApi.setGridOption(
      "columnDefs",
      this.applyRowDragColDef(applyErrorFieldCellClassRules(this.hydrateColDefs(columnDefs)))
    );
    this.autoSizeNonFlexColumns();
    this.pendingAutoSize = true;
  }

  private attachDatasource(gridDataUrl: string) {
    if (!this.gridApi) return;
    const datasource = new ServerSideDatasource(
      gridDataUrl,
      () => this.searchTextValue,
      (rows) => this.applyFooterRow(rows),
      () => this.voteBoxesHidden,
      () => this.externalFilters
    );
    this.gridApi.setGridOption("serverSideDatasource", datasource);
  }

  // Used after setState to undo any restored/poisoned visibility on structural
  // helper columns (name/period/test) without disturbing user-hideable columns.
  private enforceStructurallyHiddenColumns() {
    if (!this.gridApi) return;
    const structural = (this.gridApi.getColumnDefs() ?? []).flatMap((def) => {
      const d = def as ColDef;
      const colId = d.colId ?? d.field;
      return colId && d.hide && d.suppressColumnsToolPanel ? [{ colId, hide: true }] : [];
    });
    if (structural.length > 0) this.gridApi.applyColumnState({ state: structural });
  }

  private async loadGridState() {
    const saved = await this.fetchGridState();
    this.hasPersistedState = !!saved;
    if (!saved || !this.gridApi) return;

    const { mode, ...state } = saved;
    this.isRestoringState = true;

    // Restore the view mode before the state: advanced brings its own column defs, and setState's
    // columnOrder/visibility only mean anything against the right set of columns. The datasource is
    // attached by the caller afterwards, so this costs no extra data request. A deep-linked filter
    // preset outranks a saved basic mode — the link explicitly asked for advanced.
    if (mode === "advanced" && this.advancedColumnDefsValue.length > 0 && this.isBasicMode) {
      this.applyAdvancedMode();
      this.applyColumnDefsForCurrentMode(this.advancedColumnDefsValue);
    } else if (mode === "basic" && !this.isBasicMode && !this.modeForcedByDeepLink) {
      this.applyBasicMode();
      this.applyColumnDefsForCurrentMode(this.basicColDefs(this.columnDefsValue));
    }

    this.gridApi.setState(this.pruneUnknownColIds(state) as GridState);
    // AG Grid's `lockVisible` only blocks USER visibility changes, not the API, so
    // a persisted state could resurrect a structural helper column here.
    this.enforceStructurallyHiddenColumns();
    requestAnimationFrame(() => {
      this.isRestoringState = false;
      this.enforceStructurallyHiddenColumns();
      this.syncPanelButtonStates();
      this.updateHeaderCheckbox();
      this.syncClearFiltersButton();
    });
  }

  // Swaps column defs without touching the datasource, unlike applyViewColumnDefs.
  private applyColumnDefsForCurrentMode(columnDefs: ColDef[]) {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("columnDefs", this.applyRowDragColDef(this.hydrateColDefs(columnDefs)));
  }

  // Double-click navigation: open the row's canonical page (org edit, vote-box
  // dashboard, or the election's status-aware target). Pinned/group rows navigate nowhere.
  private handleCellDoubleClicked(event: CellDoubleClickedEvent) {
    if (!this.enableDblclickNavValue) return;
    if (event.rowPinned) return;
    const data = event.data as
      | { id?: string | number; row_type?: number; entity_id?: number; organization_id?: number; link_url?: string }
      | undefined;
    if (!data) return;

    if (event.column?.getColId() === "organization_name") {
      if (data.organization_id != null) {
        window.location.assign(`/organizations/${data.organization_id}/edit`);
      }
      return;
    }

    if (data.row_type === 1) {
      if (data.entity_id != null) {
        window.location.assign(`/vote_boxes/${data.entity_id}/dashboard`);
      }
      return;
    }

    // Same status-aware target the title anchor uses; falls back to the edit page.
    if (data.link_url) {
      window.location.assign(data.link_url);
      return;
    }
    if (data.id != null) {
      window.location.assign(`/elections/${data.id}/edit`);
    }
  }

  private handleSelectionChanged() {
    if (!this.gridApi) return;

    // getSelectedRows()/getSelectedNodes() trip AG Grid warning #199 once select-all
    // has been touched — build the row list from loaded rows' own isSelected() instead.
    const selectionState = this.gridApi.getServerSideSelectionState() as
      | { selectAll?: boolean }
      | null;
    const selectedRows = this.getLoadedSelectedRows();
    const count = selectedRows.length;
    const selectAll = selectionState?.selectAll === true;
    this.hasRowSelection = count > 0 || selectAll;

    if (!this.hasActionsMenuTarget) return;

    // `hasRowSelection` above already counts an active select-all as a selection, but the menu
    // used to gate on loaded rows alone: a select-all whose loaded nodes had not yet been marked
    // selected left the header checkbox ticked and no menu, no count, nothing — which is what a
    // customer reported as "the checkbox is enabled but doesn't seem to do anything". The two
    // conditions now agree.
    if (count > 0 || selectAll) {
      this.actionsMenuTarget.classList.add("visible");
      this.actionsMenuTarget.style.display = ""; // clear any leftover hide from scroll-tracking
      if (this.hasSelectedCountTarget) {
        // "All selected" during a select-all: `count` only ever describes the loaded blocks, so
        // printing it would under-report the selection the actions are about to act on.
        this.selectedCountTarget.textContent = selectAll ? 'All selected' : `${count} selected`;
      }
      this.renderActions(selectedRows);
      if (this.followSelectionValue) {
        this.updateFollowScrollVisibility();
      }
    } else {
      this.actionsMenuTarget.classList.remove("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = "";
      }
      this.clearActions();
      if (this.followSelectionValue) {
        this.resetMenuPosition();
      }
    }
  }

  private resolveTheme() {
    if (this.compactHeaderValue) return ebCompactHeaderTheme;
    if (this.plainHeaderValue) return ebPlainHeaderTheme;
    return ebTheme;
  }

  protected override isSelectAllActive(): boolean {
    const rawSel = this.gridApi?.getServerSideSelectionState() as { selectAll?: boolean } | null;
    return rawSel?.selectAll === true;
  }

  // No single row to anchor on during select-all — anchor on the FIRST loaded row
  // so the menu opens at the top of the grid, under the header checkbox.
  protected override getSelectAllAnchorNode(): IRowNode | null {
    if (!this.gridApi) return null;
    const displayedCount = this.gridApi.getDisplayedRowCount();
    for (let i = 0; i < displayedCount; i++) {
      const node = this.gridApi.getDisplayedRowAtIndex(i);
      if (!node || node.data === undefined) break; // stop at the loaded/unloaded boundary
      if (!node.group) return node;
    }
    return null;
  }

  // Safe stand-in for getSelectedRows()/getSelectedNodes() (AG Grid warning #199) — works
  // the same whether select-all is active or not, via each loaded row's own isSelected().
  private getLoadedSelectedRows(): Array<Record<string, unknown>> {
    if (!this.gridApi) return [];
    const rows: Array<Record<string, unknown>> = [];
    const displayedCount = this.gridApi.getDisplayedRowCount();
    for (let i = 0; i < displayedCount; i++) {
      const node = this.gridApi.getDisplayedRowAtIndex(i);
      if (!node?.data || node.group) continue;
      if (!node.isSelected()) continue;
      rows.push(node.data as Record<string, unknown>);
    }
    return rows;
  }

  // No `purge`: that option destroys all rows and resets scroll to top; omitting it
  // keeps current rows in place while every loaded block reloads in the background.
  protected override refreshAfterRemoteAction(): void {
    this.gridApi?.refreshServerSide();
  }

  private isActiveRange(ranges: CellRange[]): boolean {
    if (!ranges || ranges.length === 0) return false;
    const cellCount = ranges.reduce((total, range) => {
      if (!range.startRow || !range.endRow) return total;
      const colCount = range.columns.filter(
        (c) => c.getColId() !== "ag-Grid-SelectionColumn"
      ).length;
      if (colCount === 0) return total;
      const rowSpan = Math.abs(range.endRow.rowIndex - range.startRow.rowIndex) + 1;
      return total + rowSpan * colCount;
    }, 0);
    return cellCount > 1;
  }

  private exportHeaderName(column: Column): string {
    const colDef = column.getColDef();
    const headerName = colDef.headerName?.trim();
    if (headerName) return headerName;
    const key = String(colDef.field ?? column.getColId());
    return key.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase());
  }

  private resolveRangeSelection(ranges: CellRange[]) {
    const displayedCols = this.gridApi!.getAllDisplayedColumns() ?? [];
    const rangeColIds = new Set<string>(
      ranges.reduce<string[]>((acc, r) => acc.concat(r.columns.map((c: Column) => c.getColId())), [])
    );
    const cols = displayedCols.filter(
      (c) => rangeColIds.has(c.getColId()) && c.getColId() !== "ag-Grid-SelectionColumn"
    );

    const rowIndices = new Set<number>();
    for (const range of ranges) {
      if (!range.startRow || !range.endRow) continue;
      const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
      const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);
      for (let i = start; i <= end; i++) rowIndices.add(i);
    }

    const sortedIndices = Array.from(rowIndices).sort((a, b) => a - b);
    return { cols, sortedIndices };
  }

  private exportSelectedCellsAsCsv(ranges: CellRange[]) {
    if (!this.gridApi) return;
    const { cols, sortedIndices } = this.resolveRangeSelection(ranges);

    const csvRows: string[][] = [];
    csvRows.push(cols.map((col) => this.exportHeaderName(col)));

    for (const idx of sortedIndices) {
      const node = this.gridApi.getDisplayedRowAtIndex(idx);
      if (!node) continue;
      if (node.group) {
        const count = (node.data as Record<string, unknown>)?.childCount as number | undefined
          ?? node.allChildrenCount
          ?? 0;
        const label = `${node.key ?? ""} (${count})`;
        csvRows.push(cols.map((_, i) => (i === 0 ? label : "")));
        continue;
      }
      if (!node.data) continue;
      csvRows.push(cols.map((col) => {
        const colDef = col.getColDef();
        const data = node.data as Record<string, unknown>;
        const displayValue = resolveDisplayExportValue(colDef, data);
        if (displayValue !== undefined) return displayValue;
        const field = colDef.field;
        const val = field ? data[field] : undefined;
        if (Array.isArray(val)) return val.join(" ");
        return val != null ? String(val) : "";
      }));
    }

    const csv = "﻿" + csvRows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "export-selection.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  private resolveSelectionExportFileName(): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
    return `export-selection-${stamp}.xlsx`;
  }

  private exportSelectedCellsAsXlsx(ranges: CellRange[]) {
    if (!this.gridApi) return;
    const { cols, sortedIndices } = this.resolveRangeSelection(ranges);

    const allowedIds = new Set<string>(
      sortedIndices
        .map((idx) => this.gridApi!.getDisplayedRowAtIndex(idx)?.id)
        .filter((id): id is string => id !== undefined)
    );

    // Override cell resolution like the CSV path above (native export reads the raw status_label
    // array); pair with processRowGroupCallback or AG Grid's default group-path rendering breaks.
    this.gridApi.exportDataAsExcel({
      columnKeys: cols,
      shouldRowBeSkipped: (params) => !allowedIds.has(params.node.id ?? ""),
      fileName: this.resolveSelectionExportFileName(),
      processCellCallback: (params: ProcessCellForExportParams) => {
        const data = params.node?.data as Record<string, unknown> | undefined;
        return resolveDisplayExportValue(params.column.getColDef(), data) ?? formatCellValue(params);
      },
      processHeaderCallback: (params) => this.exportHeaderName(params.column),
      processRowGroupCallback: (params) => formatGroupCellValue(params),
    });
  }

  // Hard reset, must run after the colDefs swap. pivotMode is a grid option, not column
  // state, so it's reset explicitly too — otherwise Count stays visible after mode switch.
  // `declaredDefs` is the incoming tier's server-authored defs: getColumnDefs() reports the
  // LIVE state, so a colId shared with the outgoing tier reads back that tier's `hide`.
  private resetGridState(declaredDefs?: ColDef[]) {
    if (!this.gridApi) return;
    this.gridApi.setFilterModel({});
    this.gridApi.setGridOption("pivotMode", false);
    // A blanket `defaultState: { hide: false }` would force structurally-hidden helper
    // columns visible, so explicitly re-apply each column's own `hide` instead.
    const source = (declaredDefs ?? this.gridApi.getColumnDefs() ?? []) as ColDef[];
    const hideState = source.flatMap((def) => {
      const colId = def.colId ?? def.field;
      return colId ? [{ colId, hide: def.hide ?? false }] : [];
    });
    this.gridApi.applyColumnState({
      state: hideState,
      defaultState: { sort: null, rowGroup: false, rowGroupIndex: null, hide: false },
    });
    // Clearing the model empties the filter cards but leaves them standing, so a Basic<->Advanced
    // round trip (applyViewColumnDefs calls this) would otherwise come back to a stack of blank
    // cards. Same reset the panel header's "Clear all" performs.
    this.resetFilterPanelCards();
    this.syncCountColumnForPivotMode();
  }

  // Count (childCount) only shows, as an aggregated Values column, while Pivot Mode is on.
  // suppressColumnsToolPanel is a colDef property, not column state, so it needs updateGridOptions.
  private syncCountColumnForPivotMode() {
    if (!this.gridApi) return;
    const isPivot = this.gridApi.isPivotMode();
    this.gridApi.applyColumnState({
      state: [{ colId: "childCount", hide: !isPivot, aggFunc: isPivot ? "count" : null }],
    });
    const columnDefs = this.gridApi.getColumnDefs();
    if (!columnDefs) return;
    // Re-setting columnDefs re-applies every DECLARED `flex`, discarding the widths autosize (or
    // the user) settled on -- in Advanced the columns already overflow the viewport, so flex has
    // nothing left to distribute and every flex column collapses to minWidth. Only pay that when a
    // childCount column exists AND its tool-panel visibility actually has to flip.
    const needsToolPanelFlip = columnDefs.some(
      (def) =>
        "field" in def && def.field === "childCount" && def.suppressColumnsToolPanel !== !isPivot
    );
    if (!needsToolPanelFlip) return;

    const updated = columnDefs.map((def) =>
      "field" in def && def.field === "childCount"
        ? { ...def, suppressColumnsToolPanel: !isPivot }
        : def
    );
    this.gridApi.updateGridOptions({ columnDefs: updated });
  }

  // The grip must only appear while the Vote Boxes panel is visible — a callback
  // (not a static `true`) reads `this.voteBoxesHidden` live on every re-render.
  private applyRowDragColDef(columnDefs: ColDef[]): ColDef[] {
    if (!this.enableRowDragValue || columnDefs.length === 0) return columnDefs;
    const [first, ...rest] = columnDefs;
    return [{ ...first, rowDrag: () => this.voteBoxesHidden }, ...rest];
  }

  // applyErrorFieldCellClassRules lives in ag_grid_shared/error_field_cell_class_rules.ts so the
  // voter-list grids import the SAME function — see that module's doc comment. Composed around
  // `hydrateColDefs` at both call sites above.

  // A master-detail child grid only holds the master's children — never master rows — so the status
  // filter's "Vote box" row-type toggle would be inert there. Reuse the master colDefs without it.
  private detailColumnDefs(): ColDef[] {
    return this.applyToolPanelHeaderNames(this.columnDefsValue ?? []).map((col) => {
      const filterDef = col.filter as { component?: string } | undefined;
      if (filterDef?.component !== "statusTestFilter") return col;

      return {
        ...col,
        filterParams: {
          ...((col.filterParams as Record<string, unknown>) ?? {}),
          showVoteBoxes: false,
        },
      };
    });
  }

  // Single hydration seam for server-declared colDefs: everything that has to become a real
  // JS callback before AG Grid sees it. Callers still layer the actions formatter / row-drag
  // grip on top themselves, because those differ per call site.
  // Recurses into ColGroupDef.children first: a grouped grid declares blankSentinel /
  // toolPanelHeaderName on the leaves, which a flat map would never reach.
  private hydrateColDefs(columnDefs: ColDef[]): ColDef[] {
    const flattenedGroups = columnDefs.map((col) => {
      const children = (col as { children?: unknown }).children;
      if (!Array.isArray(children)) return col;
      return { ...col, children: this.hydrateColDefs(children as ColDef[]) };
    });
    return this.applyToolPanelHeaderNames(this.applyFilterValueCallbacks(flattenedGroups));
  }

  // A column can want a blank header in the grid but a real name everywhere a blank label is
  // useless — the Choose Columns panel (where it otherwise shows as a nameless phantom row),
  // the row-group/pivot drop zones, and export headers. `toolPanelHeaderName` is our own
  // colDef key, so it is stripped here rather than handed to AG Grid as an unknown property.
  private applyToolPanelHeaderNames(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((col) => {
      const { toolPanelHeaderName, ...rest } = col as ColDef & { toolPanelHeaderName?: string };
      if (!toolPanelHeaderName) return col;

      const gridHeader = rest.headerName ?? "";
      return {
        ...rest,
        headerValueGetter: (params: { location?: string | null }) =>
          params.location === "header" ? gridHeader : toolPanelHeaderName,
      };
    });
  }

  private applyFilterValueCallbacks(columnDefs: ColDef[]): ColDef[] {
    const withBlankSentinel = columnDefs.map((col) => {
      const params = col.filterParams as Record<string, unknown> | undefined;
      if (!params?.blankSentinel) return col;
      return {
        ...col,
        filterParams: {
          ...params,
          keyCreator: (p: { value: string | null }) => (p.value == null || p.value === "" ? BLANK_FILTER_KEY : p.value),
          // Display only — the underlying cell still renders blank; this just labels
          // the filter panel's checkbox instead of showing an empty/blank row.
          valueFormatter: (p: { value: string | null }) => (p.value == null || p.value === "" ? "(Blank)" : p.value ?? ""),
        },
      };
    });

    if (!this.filterValuesUrlValue) return withBlankSentinel;
    return withBlankSentinel.map((col) => {
      if (col.filter === "agSetColumnFilter") {
        if ((col.filterParams as Record<string, unknown>)?.values) return col;
        const colId = String(col.field ?? col.colId ?? "");
        return {
          ...col,
          filterParams: {
            ...ebFilterParams,
            ...((col.filterParams as Record<string, unknown>) ?? {}),
            values: (params: { success: (v: string[]) => void }) => {
              fetch(
                `${this.filterValuesUrlValue}?column=${encodeURIComponent(colId)}`,
                { credentials: "same-origin", headers: { Accept: "application/json" } }
              )
                .then((r) => r.json())
                .then((data: { values: string[]; counts?: Record<string, number> }) => {
                  // Rides the SAME fetch/response the values above come from — no
                  // second round-trip for counts.
                  this.asyncFilterCountsCache.set(colId, data.counts ?? {});
                  params.success(data.values ?? []);
                })
                .catch(() => params.success([]));
            },
            // Display only — never touches the underlying filterModel value. Missing/
            // not-yet-loaded count falls back to the bare value.
            valueFormatter: (p: { value: string | null }) => {
              const value = p.value ?? "";
              const count = this.asyncFilterCountsCache.get(colId)?.[value];
              return count != null ? `${value} (${count})` : value;
            },
          },
        };
      }

      // Composite Status+Test filter isn't an agSetColumnFilter, so the branch above
      // never touches it — inject filterValuesUrl so it can fetch its own counts.
      const filterDef = col.filter as { component?: string } | undefined;
      if (filterDef && typeof filterDef === "object" && filterDef.component === "statusTestFilter") {
        return {
          ...col,
          filterParams: {
            ...((col.filterParams as Record<string, unknown>) ?? {}),
            statusCountsUrl: this.filterValuesUrlValue,
          },
        };
      }

      return col;
    });
  }

  private resolveSelectionExportOptions(): ExportOptions | null {
    if (!this.gridApi) return null;

    // Check selectAll first — calling getSelectedNodes() while it's active logs a warning.
    const rawSel = this.gridApi.getServerSideSelectionState() as { selectAll?: boolean } | null;
    if (rawSel?.selectAll === true) return null;

    const selectedNodes = this.gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return null;

    // Whole groups are re-queried server-side, not enumerated client-side — SSRM only has
    // the currently-fetched block(s) loaded, so building row_ids here could truncate a large group.
    const groupNodes = selectedNodes.filter((n) => n.group && n.key != null);
    // Drop any path that's a prefix of a deeper selected path (parent auto-checked by a child).
    const allPaths = groupNodes.map((node) => this.buildGroupPath(node));
    const selectedGroupPaths = allPaths.filter(
      (path) => !allPaths.some(
        (other) => other.length > path.length && path.every((key, i) => other[i] === key)
      )
    );

    // Pair each leaf's real id with its checked ancestor path, deduped via
    // Map<id, Set<pathKey>> since one leaf can be checked under two branches.
    const leafGroupPaths = new Map<string, Set<string>>();
    for (const node of selectedNodes) {
      if (node.group) continue;
      const id = (node.data as Record<string, unknown> | undefined)?.id;
      if (id == null) continue;
      const idStr = String(id);
      if (!leafGroupPaths.has(idStr)) leafGroupPaths.set(idStr, new Set());
      const path = node.parent ? this.buildGroupPath(node.parent) : [];
      if (path.length > 0) leafGroupPaths.get(idStr)!.add(JSON.stringify(path));
    }
    const leafIds = Array.from(leafGroupPaths.keys());

    if (selectedGroupPaths.length === 0 && leafIds.length === 0) return null;

    const leafGroupPathsPayload: Record<string, string[][]> = {};
    for (const [id, pathKeys] of leafGroupPaths) {
      if (pathKeys.size === 0) continue;
      leafGroupPathsPayload[id] = Array.from(pathKeys).map((key) => JSON.parse(key) as string[]);
    }

    // Send both together — the backend ORs and dedupes them (Generate#build_filtered_relation).
    // row_group_cols keeps group headers rendering instead of collapsing to a flat list.
    return {
      mode: selectedGroupPaths.length > 0 ? "all" : "rows",
      filter_model: this.gridApi.getFilterModel() as Record<string, unknown>,
      row_group_cols: this.getRowGroupColDefs(),
      selected_group_paths: selectedGroupPaths.length > 0 ? selectedGroupPaths : undefined,
      row_ids: leafIds.length > 0 ? leafIds : undefined,
      leaf_group_paths: Object.keys(leafGroupPathsPayload).length > 0 ? leafGroupPathsPayload : undefined,
      search_text: this.searchTextValue || undefined,
      pivot_mode: this.gridApi.isPivotMode(),
      advanced: !this.isBasicMode,
    };
  }

  private buildGroupPath(node: IRowNode): string[] {
    const path: string[] = [];
    let current: IRowNode | null = node;
    while (current?.group && current.key != null) {
      path.unshift(String(current.key));
      current = current.parent ?? null;
    }
    return path;
  }

  // Basic view gets cellSelection forced off when disableBasicCellSelectionValue is set;
  // Advanced view always gets whatever grid_options.cellSelection says.
  private buildCellSelection(): GridOptions["cellSelection"] {
    if (this.disableBasicCellSelectionValue && this.isBasicMode) return false;
    return this.gridOptionsValue.cellSelection as GridOptions["cellSelection"];
  }

  // AG Grid's own clipboard (Ctrl+C, right-click Copy) needs cellSelection; turning on
  // browser text selection disables it. So this mirrors buildCellSelection exactly, strictly
  // `=== false` — only tiers that gave up cellSelection get text selection instead of it.
  private buildCellTextSelection(): boolean {
    return this.buildCellSelection() === false;
  }

  private updateHeaderCheckbox() {
    if (!this.gridApi) return;
    if (!this.enableHeaderCheckboxOnFilterValue) return;
    const filterModel = this.gridApi.getFilterModel();
    const hasFilters = Object.keys(filterModel).length > 0;
    const hasGroups = (this.gridApi.getRowGroupColumns() ?? []).length > 0;
    const hasSearch = !!this.searchTextValue;
    const showHeader = hasFilters || hasGroups || hasSearch || this.alwaysShowHeaderCheckboxValue;

    const currentRowSel = (this.gridOptionsValue.rowSelection as Record<string, unknown>) ?? {};
    // setGridOption REPLACES rowSelection wholesale, so every key the initial build set has to be
    // restated here or it is silently dropped. isRowSelectable was: a grid that opts into
    // enableHeaderCheckboxOnFilter lost its row-selectable gate the first time a filter, a search
    // or a row-group changed, which on a mixed-row grid means vote box rows quietly become
    // selectable mid-session. Keep this list in sync with the rowSelection built in connect().
    this.gridApi.setGridOption("rowSelection", {
      mode: "multiRow",
      checkboxes: true,
      ...currentRowSel,
      headerCheckbox: showHeader,
      isRowSelectable: this.voteBoxAwareRowSelectableCallback,
    });
  }

  // The two dimensions only this grid's chip resets: its quick-search box, and a sort that differs
  // from the seeded default — the default alone must NOT show the chip, being nothing the user did.
  protected override hasActiveGridState(): boolean {
    return super.hasActiveGridState() || !!this.searchTextValue || this.hasNonDefaultSort();
  }

  // True only when the current sort differs from the default sort_options entry.
  private hasNonDefaultSort(): boolean {
    const current = this.currentSortState();
    if (!current) return false;
    const def = this.sortOptionsValue.find((o) => o.default_dir);
    if (def && current.colId === def.col_id && current.sort === (def.default_dir ?? "asc")) {
      return false;
    }
    return true;
  }

  // Returns the grid fully to its DEFAULT state: filters/sort/grouping/search/pivot
  // cleared, then re-applies the default sort and refreshes server side.
  clearAllFilters() {
    if (!this.gridApi) return;
    this.resetGridState();
    if (this.hasSearchInputTarget) this.searchInputTarget.value = "";
    this.searchTextValue = "";
    // Or a refresh right after Clear filters would reinstate the term from the stale URL.
    this.syncSearchToUrl();
    if (this.gridApi.isPivotMode()) {
      this.gridApi.setGridOption("pivotMode", false);
    }
    this.applyDefaultSortIfUnset();
    this.resetRowActionsCache();
    this.gridApi.refreshServerSide({ purge: true });
    this.updateHeaderCheckbox();
    this.syncClearFiltersButton();
    // Rebuild the filter-menu dropdown from the now-cleared model.
    this.renderFilterMenu();
  }

  private getRowGroupColDefs(): Array<{ id: string; field: string; displayName: string }> {
    if (!this.gridApi) return [];
    return (this.gridApi.getRowGroupColumns() ?? []).map((col) => ({
      id: col.getColId(),
      field: String(col.getColDef().field ?? col.getColId()),
      displayName: String(col.getColDef().headerName ?? col.getColId()),
    }));
  }

  // Mirrors DropdownActionsCellRenderer#init()'s guard so a group row cleanly no-ops.
  private openRowActionsMenu(node: IRowNode): void {
    if (!this.gridApi || node.group) return;
    const rowActions = (node.data as Record<string, unknown> | undefined)?.row_actions;
    if (!Array.isArray(rowActions) || rowActions.length === 0) return;

    // AG-Grid column-virtualizes off-screen cells, so row_actions has no live renderer
    // instance yet on wide grids until scrolled into view — force it visible first.
    this.gridApi.ensureColumnVisible("row_actions");

    const instances = this.gridApi.getCellRendererInstances({ columns: ["row_actions"], rowNodes: [node] });
    for (const instance of instances) {
      if (instance instanceof DropdownActionsCellRenderer) instance.openMenu();
    }
  }
}
